// 24/10/2026 : intervenants multiples et projets SOPlanning (onglet Planification + Paramètres > Projets).
// Plusieurs intervenants = « tâches liées » de SOPlanning : une tâche par intervenant, même link_id que la
// tâche de la demande (requestPlanning.taskId). La tâche de la demande (intervenant principal) est gérée
// comme avant ; ce fichier ne s'occupe que des tâches liées des autres intervenants.
import moment from "moment";
import {
  getSoPlanningTasks,
  getSoPlanningTask,
  addSoPlanningLinkedTask,
  deleteSoPlanningTask,
  saveSoPlanningProject,
} from "../../../store/external/soplanning";

const dataOf = (r) => (r && r.data && (r.data.data !== undefined ? r.data.data : r.data)) || null;
const lc = (s) => String(s == null ? "" : s).trim().toLowerCase();
const D = "YYYY-MM-DD";

// Tâches liées à la tâche de la demande (même link_id), sans la tâche elle-même.
// Ne rejette jamais : renvoie { ok: false } si la lecture a échoué (on ne supprimera alors rien).
export const findLinkedTasks = (rp) => {
  if (!rp || !rp.taskId) return Promise.resolve({ ok: true, main: null, linked: [] });
  return getSoPlanningTask(rp.taskId)
    .then((r) => {
      const main = dataOf(r);
      if (!main || !main.link_id || !main.start_date) return { ok: true, main: null, linked: [] };
      const from = moment(main.start_date, D).subtract(31, "days").format(D);
      const to = moment(main.start_date, D).add(31, "days").format(D);
      return getSoPlanningTasks(from, to).then((r2) => {
        const tasks = dataOf(r2) || [];
        const linked = (Array.isArray(tasks) ? tasks : []).filter(
          (t) => t && t.link_id === main.link_id && String(t.task_id) !== String(main.task_id)
        );
        return { ok: true, main, linked };
      });
    })
    .catch((e) => {
      const st = e && e.response && e.response.status;
      // 400 « taskID not found » : la tâche de la demande n'existe plus, donc plus de tâches liées connues
      if (st === 400 || st === 404) return { ok: true, main: null, linked: [] };
      return { ok: false, main: null, linked: [] };
    });
};

// Identifiant d'un intervenant tel qu'il figure dans la liste des intervenants (casse)
export const userValue = (users, id) => {
  const f = (users || []).find((u) => lc(u.value) === lc(id));
  return f ? f.value : id;
};

export const uniqueUsers = (ids, exclude) => {
  const seen = {};
  const out = [];
  (ids || []).forEach((u) => {
    const k = lc(u);
    if (!k || k === lc(exclude) || seen[k]) return;
    seen[k] = true;
    out.push(u);
  });
  return out;
};

// Après enregistrement de la tâche principale : crée / déplace / retire les tâches liées.
// mainData = réponse SOPlanning de la tâche principale ; before = résultat de findLinkedTasks avant la modification.
// Renvoie une promesse (jamais rejetée) avec la liste des échecs (texte).
// 24/10/2026 : motif renvoye par SOPlanning en cas d'echec (repris dans le message de l'appli)
const errMsg = (e) => { const m = e && e.response && e.response.data && (e.response.data.message || e.response.data.error); return m ? " : " + String(m).slice(0, 80) : ""; };
export const syncLinkedTasks = (mainData, before, extras, task) => {
  const linkId = mainData && mainData.link_id;
  const fails = [];
  if (!linkId) {
    if ((extras || []).length) fails.push("tâche principale sans lien (link_id) : autres intervenants non ajoutés");
    return Promise.resolve(fails);
  }
  const want = uniqueUsers(extras, mainData.user_id);
  const remaining = ((before && before.linked) || []).slice();
  const ops = [];
  want.forEach((u) => {
    const i = remaining.findIndex((t) => lc(t.user_id) === lc(u));
    const ex = i >= 0 ? remaining.splice(i, 1)[0] : null;
    const t = {
      taskId: ex ? ex.task_id : null,
      userId: u,
      projectId: mainData.project_id || task.projectId,
      linkId,
      startDate: mainData.start_date || task.startDate,
      endDate: mainData.end_date || task.endDate || task.startDate,
      statusId: (ex && ex.status_id) || task.statusId,
      title: mainData.title || task.title,
      creatorId: task.creatorId,
    };
    ops.push(() => addSoPlanningLinkedTask(t).catch((e) => { fails.push(u + " (ajout" + errMsg(e) + ")"); }));
  });
  // retrait des intervenants enlevés — seulement si la liste d'origine a bien été lue
  if (before && before.ok) {
    remaining.forEach((t) => {
      ops.push(() => deleteSoPlanningTask(t.task_id).catch((e) => { fails.push(t.user_id + " (retrait" + errMsg(e) + ")"); }));
    });
  }
  // 24/10/2026 : envoi un par un (en simultane, SOPlanning refusait une creation : erreur 500 vue sur la 21397)
  return ops.reduce((acc, op) => acc.then(op), Promise.resolve()).then(() => fails);
};

// ---------- projets ----------
export const PROJECT_COLORS = ["1f6fd1", "2a9d3a", "f28e1c", "8e44ad", "13a8b8", "d62728", "c8961a", "e0569b", "0f7b6c", "5d6d7e", "9a9a12", "b8238f"];

// Code projet (projet_id SOPlanning : lettres et chiffres, 20 max) tiré du libellé, unique
// ex. « Réparation fuite » → REPARFUITE, « Maintenance adoucisseur » → MAINTADOUC, « Plomberie » → PLOMBERIE
const STOP = { DE: 1, DU: 1, DES: 1, LA: 1, LE: 1, LES: 1, ET: 1, L: 1, D: 1, A: 1, AU: 1, AUX: 1, EN: 1 };
export const makeProjectId = (name, existingIds) => {
  const words = String(name || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toUpperCase().split(/[^A-Z0-9]+/).filter((w) => w && !STOP[w]);
  let base = words.length > 1 ? words.slice(0, 3).map((w) => w.slice(0, 5)).join("").slice(0, 15) : (words[0] || "").slice(0, 12);
  if (!base) base = "PROJET";
  const taken = {};
  (existingIds || []).forEach((x) => { taken[lc(x)] = true; });
  let id = base, n = 2;
  while (taken[lc(id)]) { id = base.slice(0, 12) + n; n++; }
  return id;
};

export const nextProjectColor = (projects) => {
  const used = {};
  (projects || []).forEach((p) => { used[lc(p.color)] = true; });
  return PROJECT_COLORS.find((c) => !used[lc(c)]) || PROJECT_COLORS[(projects || []).length % PROJECT_COLORS.length];
};

// Création (ou modification si l'identifiant existe) d'un projet SOPlanning. Renvoie le projet { id, name, color }.
export const createProject = (p) => {
  const name = String(p.name || "").trim();
  if (!name) return Promise.reject(new Error("Libellé vide"));
  if (name.length > 50) return Promise.reject(new Error("Libellé trop long (50 caractères maximum)"));
  if (!/^[A-Za-z0-9]{1,20}$/.test(p.id || "")) return Promise.reject(new Error("Code invalide (lettres et chiffres, 20 maximum)"));
  return saveSoPlanningProject(Object.assign({}, p, { name })).then((r) => dataOf(r) || { id: p.id, name, color: p.color });
};



// WEBPACK FOOTER //
// ./src/containers/Request/Tab/planningExtras.js